import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'atlas.theme';

/** Read the stored theme — default to `system` so first-time users follow
 *  their OS preference. */
function readStored(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* ignore */ }
  return 'system';
}

function osPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Compute the effective light/dark from the chosen mode + the OS hint. */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode;
  return osPrefersDark() ? 'dark' : 'light';
}

/** Apply a resolved theme to the <html> element so all `dark:` Tailwind
 *  classes flip in one go. */
function applyToDom(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.style.colorScheme = resolved;
}

// Apply once at module load so the page doesn't flash light-then-dark on
// first paint. (Tradeoff: this runs before React mounts, which is the goal.)
if (typeof window !== 'undefined') {
  applyToDom(resolveTheme(readStored()));
}

/**
 * Theme controller. Returns `{ mode, resolved, setMode }`.
 *   - `mode`     — the user's choice (light / dark / system)
 *   - `resolved` — the actual theme being rendered (light or dark)
 *   - `setMode`  — switch and persist
 *
 * When mode is `system`, the hook listens for OS-level theme changes and
 * re-applies the right class without a reload.
 */
export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(readStored);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(readStored()));

  // Apply + persist whenever `mode` changes.
  useEffect(() => {
    const next = resolveTheme(mode);
    setResolved(next);
    applyToDom(next);
    try { window.localStorage.setItem(STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  // Follow OS toggles only while mode === 'system'.
  useEffect(() => {
    if (mode !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const next = mq.matches ? 'dark' : 'light';
      setResolved(next);
      applyToDom(next);
    };
    // addEventListener is the modern API; older Safari uses addListener.
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
  }, []);

  return { mode, resolved, setMode };
}
